import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { KEY_ARROW_LEFT, KEY_ARROW_RIGHT, SCROLL_BUTTON_STEP } from '../../common/constants';
import { useSmooth, useTimelineRuler, createHorizontalScrollingHook } from '../../controllers';

/** useResizeObserver invokes `callback` when:
 *  Element is mounted.
 *  Element is resized.
 *  Callback is changed.
 *
 * Returns a ref to attach to the element.
 */
export function useResizeObserver<T extends HTMLElement>(callback: (arg1: T) => void) {
	const observer = useRef<ResizeObserver | undefined>(undefined);
	const ref = useCallback(
		(el: T | null) => {
			if (observer.current) {
				observer.current.disconnect();
				observer.current = undefined;
			}
			if (el) {
				callback(el);
				if (typeof ResizeObserver !== 'undefined') {
					observer.current = new ResizeObserver(() => callback(el));
					observer.current.observe(el);
				}
			}
		},
		[callback],
	);
	return ref;
}

const useViewportWidth = createHorizontalScrollingHook(({ viewport }) => viewport.width);

/** Listens to the key events and scroll to the left or right accordingly. */
export function useKeyHandler(target: HTMLElement | null) {
	const [viewportWidth] = useViewportWidth();
	const [smooth, { setViewportOffset, setSmooth }] = useSmooth();
	const [{ pxToMs }] = useTimelineRuler();
	const triggeredRef = useRef(false);

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			const { keyCode } = e;

			if (keyCode !== KEY_ARROW_LEFT && keyCode !== KEY_ARROW_RIGHT) {
				return;
			}

			const directionMultiplier = keyCode === KEY_ARROW_LEFT ? -1 : 1;
			const distance = viewportWidth * SCROLL_BUTTON_STEP * directionMultiplier;

			setSmooth(true);
			setViewportOffset((offset) => offset + pxToMs(distance), 'keyboardArrows');

			triggeredRef.current = true;
			e.preventDefault();
		},
		[viewportWidth, setSmooth, setViewportOffset, pxToMs],
	);

	// disable "smooth" scroll after
	useLayoutEffect(() => {
		if (smooth && triggeredRef.current) {
			setSmooth(false);
			triggeredRef.current = false;
		}
	}, [setSmooth, smooth]);

	// Adding keydown event handling
	useEffect(() => {
		if (!target) {
			return;
		}
		target.addEventListener('keydown', handleKeyDown, false);
		return () => target.removeEventListener('keydown', handleKeyDown, false);
	}, [handleKeyDown, target]);
}
